import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppContext } from '../Context/AppContext';
import { makeApiRequest } from '../Shared/Api';

export const JOB_STATUS_COLOR_OPTIONS = [
	{ value: 'default', label: 'Gray' },
	{ value: 'primary', label: 'Blue' },
	{ value: 'info', label: 'Light blue' },
	{ value: 'warning', label: 'Orange' },
	{ value: 'success', label: 'Green' },
	{ value: 'error', label: 'Red' },
];

let cachedStatuses = null;
let cachedOrgId = null;

export const clearJobStatusCache = () => {
	cachedStatuses = null;
	cachedOrgId = null;
};

export const useJobStatuses = () => {
	const { state } = useAppContext();
	const orgId = state.user.organization?.id;
	const [statuses, setStatuses] = useState([]);
	const [loading, setLoading] = useState(Boolean(orgId));

	const fetchStatuses = useCallback(async () => {
		if (!orgId) {
			setStatuses([]);
			setLoading(false);
			return [];
		}
		setLoading(true);
		try {
			const response = await makeApiRequest('GET', '/jobs/job-statuses/');
			const next = response || [];
			setStatuses(next);
			cachedStatuses = next;
			cachedOrgId = orgId;
			return next;
		} catch (error) {
			return [];
		} finally {
			setLoading(false);
		}
	}, [orgId]);

	useEffect(() => {
		if (!orgId) {
			setStatuses([]);
			setLoading(false);
			return;
		}
		if (cachedOrgId === orgId && cachedStatuses) {
			setStatuses(cachedStatuses);
			setLoading(false);
			return;
		}
		fetchStatuses();
	}, [orgId, fetchStatuses]);

	const labels = useMemo(() => {
		const map = {};
		statuses.forEach((status) => {
			map[status.slug] = status.label;
		});
		return map;
	}, [statuses]);

	const colors = useMemo(() => {
		const map = {};
		statuses.forEach((status) => {
			map[status.slug] = status.color;
		});
		return map;
	}, [statuses]);

	const options = useMemo(
		() =>
			statuses.map((status) => ({
				value: status.slug,
				label: status.label,
			})),
		[statuses],
	);

	return {
		statuses,
		labels,
		colors,
		options,
		loading,
		refresh: fetchStatuses,
	};
};

export default useJobStatuses;
